import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePromoterAuth } from '../../contexts/PromoterAuthContext';
import { useEmpresaAuth } from '../../contexts/EmpresaAuthContext';
import Layout from '../../components/shared/Layout/Layout';
import Loading from '../../components/shared/Loading/Loading';
import Button from '../../components/shared/Button/Button';
import { useConfirm } from '../../components/shared/ConfirmDialog/ConfirmDialog';
import { ErrorMessage } from '../../components/shared/Form/Form.styles';
import { SalesContainer, PageHead, Panel, Notice } from '../sales/Sales.styles';
import { statusMessage } from '../../components/promoters/promoterLabels';
import { requestEmpresaPromoterProfile } from '../../services/promoterApi';
import PromoterPanel from './PromoterPanel';
import PromoterStatus from './PromoterStatus';
import useEmpresaPromoterMe from './useEmpresaPromoterMe';

// Entrada del panel de promotor.
//   mode 'persona' : /promotor — promotor independiente (token `promoter-token`).
//   mode 'empresa' : /empresa/promotor — la empresa dueña, con SU token de empresa
//                    (sin segundo login). Si aún no tiene perfil, puede solicitarlo.
const PromoterHome = ({ mode }) => {
  const persona = mode === 'persona';
  const navigate = useNavigate();
  const auth = usePromoterAuth();
  const { empresa } = useEmpresaAuth();
  const empresaMe = useEmpresaPromoterMe(!persona && Boolean(empresa));
  const { confirm, dialog } = useConfirm();
  const [refreshing, setRefreshing] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestedMessage, setRequestedMessage] = useState('');

  // --- puertas de entrada -------------------------------------------------
  useEffect(() => {
    if (persona && !auth.loading && !auth.isAuthenticated && !auth.connectionError) {
      navigate('/promotor/login', { replace: true });
    }
    if (!persona && !empresa) navigate('/empresa/login', { replace: true });
  }, [persona, auth.loading, auth.isAuthenticated, auth.connectionError, empresa, navigate]);

  const headerType = persona ? 'promoter' : 'empresa';
  const loading = persona ? auth.loading : empresaMe.loading;
  const me = persona ? auth.me : empresaMe.me;
  const connectionError = persona ? (!me ? auth.connectionError : '') : empresaMe.error;

  const refreshMe = useCallback(async () => {
    setRefreshing(true);
    try {
      if (persona) await auth.refresh();
      else await empresaMe.refresh();
    } catch (e) {
      // el mensaje queda en connectionError
    } finally {
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona, auth.refresh, empresaMe.refresh]);

  // Refresco silencioso (contadores) sin parpadeo de "Actualizando".
  const refreshQuiet = useCallback(() => {
    Promise.resolve(persona ? auth.refresh() : empresaMe.refresh()).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona, auth.refresh, empresaMe.refresh]);

  const handleSessionError = useCallback((err) => {
    if (persona) auth.endSession(err.message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona, auth.endSession]);

  const handleRequestProfile = async () => {
    const ok = await confirm({
      title: '¿Solicitar tu perfil de promotor?',
      message: (
        <>
          <p>
            Tu empresa podrá vender las actividades de <strong>otras empresas</strong> con su enlace y ganar una comisión,
            además de seguir vendiendo las suyas.
          </p>
          <p>BokerHub debe <strong>aprobar</strong> tu perfil y fijar tu porcentaje de comisión antes de que puedas operar.</p>
        </>
      ),
      confirmLabel: 'Solicitar perfil',
    });
    if (!ok) return;
    setRequestError('');
    setRequesting(true);
    try {
      const data = await requestEmpresaPromoterProfile();
      setRequestedMessage(data.message || 'Solicitud enviada. BokerHub revisará tu perfil de promotor.');
      await empresaMe.refresh();
    } catch (err) {
      if (err.status === 409) {
        // ya tenía perfil: se vuelve a leer
        await empresaMe.refresh();
      } else {
        setRequestError(err.message);
      }
    } finally {
      setRequesting(false);
    }
  };

  if ((persona && !auth.loading && !auth.isAuthenticated && !auth.connectionError) || (!persona && !empresa)) {
    return <Loading text="Cargando..." />;
  }

  if (loading) {
    return (
      <Layout headerType={headerType}>
        <Loading text="Cargando tu panel de promotor..." />
      </Layout>
    );
  }

  const promoter = me && me.promoter;
  const blocked = promoter && (promoter.status !== 'approved' || me.canOperate === false);

  return (
    <Layout headerType={headerType}>
      <SalesContainer>
        <PageHead>
          <h1>{persona ? 'Panel de promotor' : 'Vender como promotor'}</h1>
          <p>
            {persona
              ? 'Pide vender las actividades de empresas, comparte tus enlaces y revisa tus ventas y cortes.'
              : 'Tu empresa también puede vender las actividades de otras empresas con su enlace de promotor.'}
          </p>
          {!persona && (
            <p style={{ marginTop: 6 }}>
              <Link to="/empresa/promotores">← Volver a los promotores de mi empresa</Link>
            </p>
          )}
        </PageHead>

        {connectionError && !me && (
          <Panel>
            <ErrorMessage role="alert">{connectionError}</ErrorMessage>
            <Button onClick={refreshMe} disabled={refreshing}>Reintentar</Button>
          </Panel>
        )}

        {/* Empresa sin perfil de promotor */}
        {!persona && empresaMe.notAPromoter && (
          <Panel>
            <h2>Solicita tu perfil de promotor</h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 14 }}>
              Con un perfil de promotor tu empresa puede pedir vender las actividades de otras empresas y recibir una comisión por
              cada venta hecha con su enlace. Es el mismo inicio de sesión que ya usas: no necesitas otra cuenta.
              BokerHub revisa y aprueba cada perfil antes de que pueda operar.
            </p>
            {requestError && <ErrorMessage role="alert">{requestError}</ErrorMessage>}
            <Button onClick={handleRequestProfile} disabled={requesting}>
              {requesting ? 'Enviando…' : 'Solicitar perfil de promotor'}
            </Button>
          </Panel>
        )}

        {requestedMessage && promoter && promoter.status === 'pending' && (
          <Notice role="status">{requestedMessage}</Notice>
        )}

        {promoter && blocked && promoter.status !== 'approved' && (
          <PromoterStatus promoter={promoter} onRefresh={refreshMe} refreshing={refreshing} />
        )}

        {promoter && promoter.status === 'approved' && me.canOperate === false && (
          <Notice $warn role="status">
            Tu perfil está aprobado, pero por ahora no puede operar.{' '}
            {statusMessage(promoter) ? '' : 'Si crees que es un error, escribe a soporte.'}
          </Notice>
        )}

        {promoter && !blocked && (
          <PromoterPanel
            mode={mode}
            me={me}
            onSessionError={handleSessionError}
            onRefreshMe={refreshQuiet}
          />
        )}
      </SalesContainer>
      {dialog}
    </Layout>
  );
};

export default PromoterHome;
